/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { SyntheticEvent } from 'react'
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar'
import Close from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import InfoIcon from '@mui/icons-material/Info'

import IconButton, { ColorVariant } from '../../buttons/IconButton/IconButton'
import SecondaryButton from '../../buttons/SecondaryButton'
import {
  darkBackgroundLinkColor,
  darkBackgroundIconColor,
  snackbarSuccessColor,
  snackbarErrorColor,
  snackbarInfoColor
} from '../../../../lib/styles/colors'
import { tertiaryBodyCss } from '../../../../lib/styles/typography'
import { NotificationType } from '../../../../types'

const snackbarCss = css`
  &.MuiSnackbar-root {
    z-index: 9900;
  }
`

const messageContainerCss = css`
  align-items: center;
  display: flex;

  ${tertiaryBodyCss}
  color: ${darkBackgroundIconColor};
`

const messageCss = css`
  padding-left: 14px;
`

export type SimpleSnackbarProps = {
  /** Message to display */
  message: string
  /** Callback event when clicking close */
  onClose: () => void
  /** Function to retry when type is error */
  onRetry?: () => void
  /** Show the Refresh button */
  refresh?: boolean | undefined
  /** Optional aria compliant label on the retry button */
  retryAria?: string | null
  /** Type of notification */
  type: NotificationType
  /** Optional aria compliant label on the View Link */
  viewAria?: string | null
  /** Optional URL paired with View Link. View Link on display when this is provided */
  viewUrl?: string | null
}

const defaultProps: Pick<
  SimpleSnackbarProps,
  'onRetry' | 'refresh' | 'retryAria' | 'viewAria' | 'viewUrl'
> = {
  onRetry: undefined,
  refresh: false,
  retryAria: '',
  viewAria: '',
  viewUrl: ''
}

const SimpleSnackbar = ({
  message,
  onClose,
  onRetry,
  refresh,
  retryAria,
  type,
  viewAria,
  viewUrl
}: SimpleSnackbarProps) => {
  const getColor = () => {
    switch (type) {
      case NotificationType.Success:
        return snackbarSuccessColor
      case NotificationType.Error:
        return snackbarErrorColor
      case NotificationType.Info:
        return snackbarInfoColor
      default:
        return darkBackgroundLinkColor
    }
  }

  const color = getColor()

  const buttonCss = css`
    color: ${color};
  `
  const closeButtonCss = css`
    color: ${darkBackgroundIconColor};
    &:hover {
      background-color: transparent;
    }
  `

  const renderIcon = () => {
    switch (type) {
      case NotificationType.Success:
        return <CheckCircleIcon htmlColor={color} />
      case NotificationType.Error:
        return <ErrorIcon htmlColor={color} />
      case NotificationType.Info:
        return <InfoIcon htmlColor={color} />
      default:
        return null
    }
  }

  const handleRefresh = () => {
    window.location.reload()
  }
  const handleClose = (_event: Event | SyntheticEvent<any, Event>, reason: SnackbarCloseReason) => {
    if (reason !== 'clickaway') {
      onClose()
    }
  }

  return (
    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
      css={snackbarCss}
      open={!!message}
      onClose={handleClose}
      message={
        <div css={messageContainerCss}>
          {renderIcon()}
          <span css={messageCss}>{message}</span>
        </div>
      }
      action={
        <div>
          {viewUrl ? (
            <SecondaryButton aria-label={viewAria} css={buttonCss} href={viewUrl}>
              View
            </SecondaryButton>
          ) : null}
          {refresh ? (
            <SecondaryButton css={buttonCss} onClick={handleRefresh}>
              Refresh
            </SecondaryButton>
          ) : null}
          {onRetry ? (
            <SecondaryButton aria-label={retryAria} css={buttonCss} onClick={onRetry}>
              Retry
            </SecondaryButton>
          ) : null}
          <IconButton
            ariaLabel="Dismiss"
            css={closeButtonCss}
            onClick={onClose}
            colorVariant={ColorVariant.Secondary}
          >
            <Close />
          </IconButton>
        </div>
      }
    />
  )
}

SimpleSnackbar.defaultProps = defaultProps

export default SimpleSnackbar
